/**
 * DebugFeedbackBlock — natural-language "debug this code" widget for
 * Agentic AI Mastery modules.
 *
 * The student reads a buggy snippet, types their proposed fix in plain
 * English, and receives Socratic, scaffolded feedback from the LLM grader —
 * never the canonical answer. Mirrors AgenticQuizBlock's design language
 * (brand purple, MUI Paper cards, Stack layout).
 *
 * State machine (spec §5): prompt → grading → feedback → resolved.
 * Transition logic lives in debugFeedbackMachine.ts so it can be unit-tested.
 */

import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme, type Theme } from "@mui/material/styles";
import BugReportIcon from "@mui/icons-material/BugReport";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import {
  DebugExercise,
  DebugFeedbackResult,
  DebugSolution,
  DebugVerdict,
  getDebugExercise,
  getDebugSolution,
  submitDebugFeedback,
} from "../api/client";
import LessonMarkdown from "./LessonMarkdown";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import {
  DebugState,
  MIN_RESPONSE_CHARS,
  canRetry,
  canSubmit,
  primaryActionLabel,
  showsRemediation,
  showsSkipOrReveal,
  verdictLabel,
} from "./debugFeedbackMachine";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface DebugFeedbackBlockProps {
  slug: string;
  exerciseId: string;
}

// Color hints per verdict (text label always present too, for a11y).
function getVerdictColor(theme: Theme): Record<DebugVerdict, string> {
  return {
    on_track: theme.palette.success.main,
    partial: theme.palette.warning.main,
    off_track: theme.palette.error.main,
    misconception_match: theme.palette.error.main,
    cannot_evaluate: theme.palette.text.secondary,
  };
}

function VerdictChip({ verdict }: { verdict: DebugVerdict }) {
  const theme = useTheme();
  const color = getVerdictColor(theme)[verdict];
  return (
    <Chip
      label={verdictLabel(verdict)}
      size="small"
      sx={{
        fontWeight: 500,
        fontSize: "0.72rem",
        bgcolor: alpha(color, 0.14),
        color,
      }}
    />
  );
}

export default function DebugFeedbackBlock({
  slug,
  exerciseId,
}: DebugFeedbackBlockProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const [state, setState] = useState<DebugState>("prompt");
  const [response, setResponse] = useState("");
  const [result, setResult] = useState<DebugFeedbackResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attemptNumber, setAttemptNumber] = useState(1);
  const [solution, setSolution] = useState<DebugSolution | null>(null);
  const [solutionLoading, setSolutionLoading] = useState(false);
  const liveRegionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const {
    data: exercise,
    isLoading,
    error: loadError,
  } = useQuery<DebugExercise>({
    queryKey: ["debug-exercise", slug, exerciseId],
    queryFn: () => getDebugExercise(slug, exerciseId),
    retry: false,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (loadError || !exercise) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {t("DebugFeedbackBlock.notAvailable")} {" "}
        {loadError ? t("DebugFeedbackBlock.signInOrUnlock") : ""}
      </Alert>
    );
  }

  async function handleSubmit() {
    if (!exercise || !canSubmit(response)) return;
    setState("grading");
    setError(null);
    try {
      const res = await submitDebugFeedback(slug, {
        check_id: exercise.check_id,
        exercise_id: exercise.exercise_id,
        student_response: response,
        attempt_number: attemptNumber,
      });
      setResult(res);
      setState("feedback");
      // Announce result for screen readers.
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = `${verdictLabel(res.verdict)}. ${res.feedback}`;
      }
    } catch (e: unknown) {
      const status =
        typeof e === "object" && e && "response" in e
          ? (e as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 429) {
        setError(t("DebugFeedbackBlock.attemptLimit"));
      } else {
        setError(t("DebugFeedbackBlock.gradingError"));
      }
      setState("prompt");
    }
  }

  function handleRetry() {
    if (!result) return;
    // cannot_evaluate doesn't count, so the attempt number stays the same.
    if (result.verdict !== "cannot_evaluate") {
      setAttemptNumber((n) => n + 1);
    }
    setResponse("");
    setResult(null);
    setError(null);
    setState("prompt");
  }

  function handleResolveSelf() {
    setState("resolved");
  }

  async function handleShowSolution() {
    if (!exercise) return;
    setSolutionLoading(true);
    setError(null);
    try {
      const sol = await getDebugSolution(slug, exercise.exercise_id);
      setSolution(sol);
    } catch {
      setError(t("DebugFeedbackBlock.solutionLoadError"));
    } finally {
      setSolutionLoading(false);
    }
  }

  function scrollToReview(anchor: string) {
    const el = document.getElementById(anchor);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 96 + 8;
    window.scrollTo({ top: y, behavior: "smooth" });
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  }

  const SectionHeader = (
    <>
      <Divider sx={{ mb: 5 }} />
      <Stack direction="row" spacing={1.5} alignItems="center" mb={3}>
        <BugReportIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
        <Typography
          variant="caption"
          fontWeight={700}
          sx={{
            color: theme.palette.primary.main,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
          }}
        >
          {t("DebugFeedbackBlock.heading")}
        </Typography>
      </Stack>
    </>
  );

  const CodeSnippet = (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: alpha(theme.palette.text.primary, 0.03),
        overflowX: "auto",
      }}
    >
      <Box
        component="pre"
        sx={{
          m: 0,
          fontFamily: "monospace",
          fontSize: "0.82rem",
          lineHeight: 1.6,
          whiteSpace: "pre",
        }}
      >
        {exercise.code_snippet}
      </Box>
    </Paper>
  );

  // Visually-hidden live region for screen-reader result announcements.
  const LiveRegion = (
    <Box
      ref={liveRegionRef}
      aria-live="polite"
      sx={{
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
        whiteSpace: "nowrap",
      }}
    />
  );

  // ---- resolved -----------------------------------------------------------
  if (state === "resolved") {
    return (
      <Box>
        {SectionHeader}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.success.main, 0.4),
            bgcolor: alpha(theme.palette.success.main, 0.04),
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main, fontSize: 22 }} />
            <Typography variant="body2" fontWeight={500}>
              {t("DebugFeedbackBlock.exerciseComplete")}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // ---- feedback -----------------------------------------------------------
  if (state === "feedback" && result) {
    return (
      <Box>
        {SectionHeader}
        {LiveRegion}
        {CodeSnippet}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(getVerdictColor(theme)[result.verdict], 0.35),
            bgcolor: alpha(getVerdictColor(theme)[result.verdict], 0.03),
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <VerdictChip verdict={result.verdict} />
            {result.verdict !== "cannot_evaluate" && (
              <Typography variant="caption" color="text.secondary">
                {t("DebugFeedbackBlock.attemptsLeft", {
                  count: result.attempts_remaining,
                })}
              </Typography>
            )}
          </Stack>

          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 2 }}>
            {result.feedback}
          </Typography>

          {result.matched_concept_tags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
              {result.matched_concept_tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.68rem",
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    color: theme.palette.primary.main,
                  }}
                />
              ))}
            </Stack>
          )}

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {canRetry(result) && (
              <Button
                variant="contained"
                onClick={handleRetry}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {primaryActionLabel(result.next_action)}
              </Button>
            )}

            {showsRemediation(result) && result.remediation_link && (
              <Button
                variant="outlined"
                component={MuiLink}
                href={result.remediation_link}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: theme.palette.primary.main,
                }}
              >
                {t("DebugFeedbackBlock.remedialDeck")}
              </Button>
            )}

            {showsSkipOrReveal(result) && !solution && (
              <Button
                variant="contained"
                onClick={handleShowSolution}
                disabled={solutionLoading}
                startIcon={
                  solutionLoading ? (
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                  ) : (
                    <LightbulbIcon />
                  )
                }
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {solutionLoading
                  ? t("DebugFeedbackBlock.loading")
                  : t("DebugFeedbackBlock.showSolution")}
              </Button>
            )}

            {showsSkipOrReveal(result) && (
              <Button
                variant="outlined"
                onClick={handleResolveSelf}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: "divider",
                  color: "text.secondary",
                }}
              >
                {t("DebugFeedbackBlock.skipForLater")}
              </Button>
            )}

            <Button
              variant="text"
              onClick={handleResolveSelf}
              sx={{ textTransform: "none", fontWeight: 500, color: theme.palette.success.main }}
            >
              {t("DebugFeedbackBlock.solvedIt")}
            </Button>
          </Stack>

          {/* Canonical solution — only reachable after the attempt cap. */}
          {solution && (
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <LightbulbIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: theme.palette.primary.main }}
                >
                  {t("DebugFeedbackBlock.detailedSolution")}
                </Typography>
              </Stack>
              <Box sx={{ "& p:first-of-type": { mt: 0 }, fontSize: "0.92rem" }}>
                <LessonMarkdown>{solution.canonical_fix}</LessonMarkdown>
              </Box>
              {solution.review && (
                <Button
                  size="small"
                  startIcon={<MenuBookIcon sx={{ fontSize: "1rem" }} />}
                  onClick={() => scrollToReview(solution.review!.anchor)}
                  sx={{ mt: 0.5, textTransform: "none", fontWeight: 500, color: theme.palette.primary.main }}
                >
                  {t("DebugFeedbackBlock.reviewLink", { label: solution.review.label })}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  // ---- prompt / grading ---------------------------------------------------
  const isGrading = state === "grading";
  return (
    <Box>
      {SectionHeader}
      {LiveRegion}
      <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
        {exercise.prompt}
      </Typography>
      {CodeSnippet}

      {!isAuthenticated ? (
        // Grading calls the LLM and is rate-limited per user, so it requires
        // auth. Rather than let an anonymous reader submit into a 401 (which
        // surfaced as a misleading "something went wrong"), show a clear CTA.
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.03),
          }}
        >
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t("DebugFeedbackBlock.signInToSubmit")}
          </Typography>
          <Button
            variant="contained"
            onClick={() =>
              navigate("/login", {
                state: { from: location.pathname + location.search },
              })
            }
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              textTransform: "none",
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
            }}
          >
            {t("DebugFeedbackBlock.signInToGetFeedback")}
          </Button>
        </Paper>
      ) : (
        <>
          <TextField
            multiline
            minRows={3}
            fullWidth
            placeholder={t("DebugFeedbackBlock.responsePlaceholder")}
            value={response}
            onChange={(e) => setResponse(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isGrading}
            inputProps={{ "aria-label": t("DebugFeedbackBlock.responseAriaLabel") }}
            sx={{ mb: 1.5 }}
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              disabled={!canSubmit(response) || isGrading}
              onClick={handleSubmit}
              startIcon={
                isGrading ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : undefined
              }
              sx={{
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                textTransform: "none",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                "&.Mui-disabled": { bgcolor: alpha(theme.palette.primary.main, 0.35), color: "white" },
              }}
            >
              {isGrading
                ? t("DebugFeedbackBlock.gradingInProgress")
                : t("DebugFeedbackBlock.getFeedback")}
            </Button>
            {!canSubmit(response) && !isGrading && (
              <Typography variant="caption" color="text.disabled">
                {t("DebugFeedbackBlock.minChars", { min: MIN_RESPONSE_CHARS })}
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}
